import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users, BookOpen, Award, Clock, CheckCircle2, AlertCircle, Building2, TrendingUp
} from "lucide-react";
import CollaboratorsCarousel from "@/components/CollaboratorsCarousel";

const STAT = (icon, label, value, color = "navy") => ({ icon, label, value, color });

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    user.role === "master_admin" && STAT(Building2, "Empresas", stats.total_companies ?? 0),
    STAT(Users, "Colaboradores", stats.total_users),
    STAT(BookOpen, "Cursos ativos", stats.total_courses),
    STAT(Clock, "Horas treinadas", `${stats.hours_trained}h`),
    STAT(Award, "Certificados", stats.total_certificates),
    STAT(CheckCircle2, "Conclusões", stats.completed_enrollments, "green"),
    STAT(AlertCircle, "Em andamento", stats.in_progress_enrollments, "red"),
    STAT(TrendingUp, "Taxa de conclusão", `${stats.completion_rate}%`, "green"),
  ].filter(Boolean) : [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header>
        <div className="eyebrow">Dashboard executivo</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-1">
          Visão geral da operação
        </h1>
        <p className="text-slate-600 mt-2 text-sm">Indicadores de RH, operacional e compliance em tempo real.</p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dashboard-stats">
        {cards.map((c, i) => (
          <Card key={i} className="border-[var(--unigr-border)]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500 uppercase tracking-wide">{c.label}</div>
                <div className={"w-8 h-8 rounded-md flex items-center justify-center " + (c.color === "green" ? "bg-emerald-50 text-emerald-700" : c.color === "red" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-[var(--unigr-navy)]")}>
                  <c.icon className="w-4 h-4" />
                </div>
              </div>
              <div className="mt-3 font-display text-3xl font-extrabold">{c.value}</div>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-[var(--unigr-border)]">
          <CardContent className="p-6">
            <div className="font-display text-lg font-bold mb-2">Compliance & Conformidade</div>
            <p className="text-sm text-slate-600">
              Acompanhe ciência de procedimentos, reciclagens em dia e auditoria. Use o módulo
              <strong> Matriz de Capacitação</strong> para visualizar o status por cargo.
            </p>
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              <Mini k="Ciência de POPs" v="Em dia" tone="green" />
              <Mini k="Reciclagens" v="0 vencidas" tone="green" />
              <Mini k="Auditoria" v="Pronta" tone="navy" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-[var(--unigr-border)]">
          <CardContent className="p-6">
            <div className="font-display text-lg font-bold mb-2">Próximas ações</div>
            <ul className="text-sm space-y-2 text-slate-700">
              <li>• Crie um curso ou use a IA para gerar a partir de um POP.</li>
              <li>• Cadastre cargos e vincule trilhas obrigatórias.</li>
              <li>• Convide colaboradores e acompanhe progresso.</li>
            </ul>
          </CardContent>
        </Card>
      </section>

      <CollaboratorsCarousel />
    </div>
  );
}

function Mini({ k, v, tone }) {
  const cls = tone === "green" ? "bg-emerald-50 text-emerald-700" : tone === "red" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-[var(--unigr-navy)]";
  return (
    <div className="rounded-md border border-[var(--unigr-border)] p-3">
      <div className="text-xs text-slate-500">{k}</div>
      <div className={"inline-block mt-1 px-2 py-0.5 rounded text-sm font-medium " + cls}>{v}</div>
    </div>
  );
}
